import React, { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { getGovernanceMetrics } from "../../lib/api";
import { layerGlyph, supervisoryGlyph } from "../../lib/glyphs";

/**
 * Live Security HUD — replaces the decorative marquee with real-time
 * governance counters in glyph form. Polls /api/governance/metrics
 * every 30s. Public endpoint — no auth required, so the HUD works
 * even for anonymous visitors.
 */
export default function SecurityHud() {
  const [m, setM] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      getGovernanceMetrics()
        .then((d) => { if (!cancelled) { setM(d); setError(false); } })
        .catch(() => { if (!cancelled) setError(true); });
    };
    tick();
    const iv = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Loading / failure → render a quiet static marquee so the header
  // never collapses.
  if (!m || error) {
    return (
      <Marquee speed={30} gradient={false} className="text-[10px] uppercase tracking-[0.35em] text-zinc-700 py-1">
        <span className="mx-8" data-testid="hud-loading">· sentinel telemetry warming up ·</span>
      </Marquee>
    );
  }

  const item = (key, glyph, label, value, tone) => (
    <span key={key} className="mx-6 inline-flex items-center gap-2" data-testid={`hud-${key}`}>
      <span className={`${tone || "text-zinc-300"}`}>{glyph}</span>
      <span className="text-zinc-500">{label}</span>
      <span className={`tabular-nums ${tone || "text-zinc-100"}`}>{value}</span>
    </span>
  );

  // Tone follows safety semantics: zero is neutral/good; non-zero on
  // safety gates is intentional drama (these gates SHOULD fire).
  const aegisTone = (m.aegis_input_blocks + m.aegis_output_blocks) > 0 ? "text-amber-300" : "text-zinc-400";
  const sealTone = "text-emerald-300";   // every reply gets a receipt — always live
  const factTone = m.triangulations_fact > 0 ? "text-emerald-300" : "text-zinc-500";
  const contestedTone = m.triangulations_contested > 0 ? "text-amber-300" : "text-zinc-500";
  const driftTone = m.drift_neutralizations > 0 ? "text-sky-300" : "text-zinc-500";
  const shiftTone = m.topic_shifts_detected > 0 ? "text-sky-300" : "text-zinc-500";
  const honestyTone = (m.honesty_low_self_rating + m.honesty_medium_self_rating) > 0 ? "text-zinc-300" : "text-zinc-500";

  return (
    <Marquee
      speed={28}
      gradient={false}
      className="text-[10px] uppercase tracking-[0.35em] py-1"
      data-testid="security-hud"
    >
      {item("manifest", "⛓", supervisoryGlyph("Reply Provenance Chain") ? "manifest" : "·", (m.manifest_hash_sha256 || "").slice(0, 10), sealTone)}
      {item("messages", "⟳", "messages", m.messages_processed, "text-zinc-300")}
      {item("aegis", layerGlyph(0), "aegis blocks", `${m.aegis_input_blocks}/${m.aegis_output_blocks}`, aegisTone)}
      {item("anchor", layerGlyph(1), "anchor", m.anchor_blocks_on_receipt, "text-zinc-300")}
      {item("sink", layerGlyph(4), "drift fixed", m.drift_neutralizations, driftTone)}
      {item("resonance", layerGlyph(5), "obs reframes", m.obsidian_reframes, "text-zinc-300")}
      {item("memory", layerGlyph(8), "topic shifts", m.topic_shifts_detected, shiftTone)}
      {item("honesty", layerGlyph(9), "hedged", `${m.honesty_low_self_rating}/${m.honesty_medium_self_rating}`, honestyTone)}
      {item("scholar", layerGlyph(10), "fact", m.triangulations_fact, factTone)}
      {item("contested", layerGlyph(10), "contested", m.triangulations_contested, contestedTone)}
      {item("seal", "⛓", "chain valid", "✓", sealTone)}
      <span className="mx-8 text-zinc-700">· v{m.pipeline_version} ·</span>
    </Marquee>
  );
}
